import * as React from "react";
import { connect } from "react-redux";
import { isEmpty as _isEmpty } from "lodash"
import { ModalActions } from "../../../actions/ModalActions";
import { IAppState } from "../../../models/IAppState";
import { FieldTypes, FormItemMode } from "../../../utils/AppConstants";
import AppUtils from "../../../utils/AppUtils";
import Questionnaire from "../../common/Questionnaire";
import CustomHeader from "../../utils/CustomHeader";
import ITreatmentPlanTemplateResponseObj from "../../../models/ITreatmentPlanTemplateResponse";
import IAppointmentSchedule from "../../../models/IAppointmentSchedule";
import FlatList from "../../common/FlatList";
import IQuestionnaire from "../../../models/util/IQuestionnaire";

export interface GenericEditorProps {
    widgetData: any,
    updatedObject: any,
    validations: any,
    validationHandler: (validations: any) => void,
    updateHandler: (updatedObject: any) => void,
    itemRemove: (updatedObject: any) => void,
    appointmentId?: number,
    uiConfiguration: any[],
    fetchPatientPastSessions: (patientId: number, subSectionKey: string) => Promise<any>,
    patientId: number,
    subSectionKey: string,
    checkData: any,
    className: string,
    onAddNewItem: () => void,
    isViewOnly?: boolean,
    modalHeaders: [],
    modalResponse: any,
    complaints: any
    suggestions: { [key: string]: any }
    onTreatmentPlanSelect: (template: ITreatmentPlanTemplateResponseObj, updatedObject: any, key: string) => void
    fetchDataOnSelect: (diagnosis?: Array<string>) => void
    emptyText?: string
    appointmentSchedule: IAppointmentSchedule
    getSuggestionsSequentially?: () => void;
}

interface GenericEditorStateToProps {
    responseData: any,
    showModal: boolean
}

interface GenericEditorDispatchToProps {
    toggleShowModal: (showModal: boolean) => void,
    updateModalUIConfig: (responseData: any) => void,
    addSymptomForReview: (name: string) => Promise<any>;
    addButtonClicked: (status: boolean, name: string) => Promise<any>,
}

interface IhideContent {
    isHidden: boolean
}

class GenericEditor extends React.Component<GenericEditorProps & GenericEditorStateToProps & GenericEditorDispatchToProps, IhideContent> {

    constructor(props: GenericEditorProps & GenericEditorStateToProps & GenericEditorDispatchToProps) {
        super(props);
        this.state = { isHidden: false };
    }

    itemRemove = (index: number, key: string) => {
        let updatedObject: any = { ...this.props.updatedObject };
        updatedObject[key].splice(index, 1);
        this.props.updateHandler(updatedObject);
    }

    itemChanged = (updatedValue: any, index: number, key: string, field?: any) => {
        let updatedObject = { ...this.props.updatedObject };
        if (key) {
            if (!updatedObject[key]) {
                updatedObject[key] = [];
            }
            updatedObject[key][index] = updatedValue.item;
            this.props.updateHandler(updatedObject);
        }

    }

    templatedListValidations = (value: any, index: number, key: string) => {
        let validations = { ...this.props.validations };
        if (key) {
            if (!validations[key]) {
                validations[key] = []
            }
            validations[key][index] = value;
            this.props.validationHandler(validations);
        }
    }

    createNewFields = (dataObj: any = { fields: [] }) => {
        let item: any = {};
        dataObj.fields.map((field: any, index: number) => {
            if (field.fieldName) {
                item[field.fieldName] = ""
                if (field.displayName) {
                    item[field.displayName] = ""
                }
            } else if (field.type === FieldTypes.TEMPLATED_LIST || field.type === FieldTypes.TEMPLATED_LIST_V3) {
                item["items"] = [];
            }
        });
        return item;
    }

    updateHandler = (value: any, key: string, field?: any) => {
        let updatedObject = { ...this.props.updatedObject, [key]: value };
        this.props.updateHandler(updatedObject);
    }

    updateValidations = (value: any, key: string, field?: any) => {
        let validations = { ...this.props.validations, [key]: value };
        this.props.validationHandler(validations);
    }

    updatePainViewHandler = (value: any, subkey: string, key: string) => {
        let updatedObject = { ...this.props.updatedObject };
        let obj: any = {};
        if (updatedObject[key]) {
            obj = { ...updatedObject[key] };
        }
        obj[subkey] = value;
        updatedObject[key] = obj;
        this.props.updateHandler(updatedObject);
    }

    toggleModal = (modalData: string = "") => {
        this.props.toggleShowModal(true);
        let responseData = AppUtils.getDeepCopiedObject(this.props.checkData.data);
        responseData.forEach((data: any) => {
            data.isViewOnly = true;
        });
        const UIConfig = {
            data: responseData, subSectionTitle: this.props.checkData.subSectionTitle, modalHeaders: this.props.modalHeaders,
            modalType: this.props.checkData.modalConfigurationJson && this.props.checkData.modalConfigurationJson.modalType,
            modalErrorText: this.props.checkData.modalConfigurationJson && this.props.checkData.modalConfigurationJson.modalErrorText,
            modalErrorDetails: this.props.checkData.modalConfigurationJson && this.props.checkData.modalConfigurationJson.modalErrorDetails,
            modalData: modalData,
            modalButtons: this.props.checkData.modalConfigurationJson && this.props.checkData.modalConfigurationJson.modalButtons
        }
        this.props.updateModalUIConfig(UIConfig);
    }

    getPastSession = (patientId: number, subSectionKey: string) => {
        this.props.fetchPatientPastSessions(patientId, subSectionKey);
        this.toggleModal();
    }

    onAddNewItem = (patientId: number, subSectionKey: string, modalData: string) => {
        // TODO: Don't delete. Might need some enhancements in the future
        // this.props.addButtonClicked(true, modalData)
        // this.toggleModal(modalData);`
    }

    handleUpdateTemplatedListData = (data: any[], key: string) => {
        let updatedObject = { ...this.props.updatedObject };
        updatedObject[key] = data;
        this.props.updateHandler(updatedObject);
    }

    render() {
        const { patientId } = this.props;
        const { subSectionDescription, data, showPastHistory, headers, subSectionKey, showPastHistoryText, subSectionType, validationSchema, subHeaderTags } = this.props.checkData;
        let subSectionClass = subSectionType && subSectionType === "fixedNonCollapsible" ? "non-collapsible-fixed-generic-editor" : "physical-assessment-editor";
        return <div className={`${subSectionClass} ${subSectionKey || ""}`}>
            {subSectionDescription &&
                <div className="subsection-description">{subSectionDescription}</div>
            }
            {!_isEmpty(subHeaderTags) && <div className={"sub-header-tags-container"}>
                <span className={"points-text"}><img src={"/images/points-to-cover-icon.svg"} />Points to cover</span>
                <FlatList listItems={subHeaderTags} renderItem={({ item }) => <span className={"tag-text"}>{item}</span>} />
            </div>}
            <div className="section-wrapper-bordered">
                {headers && <CustomHeader headerInfos={headers} rootCls={"physical-assessment"} />}
                {data.map((dataObj: IQuestionnaire, index: number) => {
                    let key = dataObj.key ? dataObj.key : dataObj.answerList && dataObj.answerList[0] && dataObj.answerList[0].key;
                    let targetKey, targetValue = "";
                    targetKey = dataObj.answerList[0].targetFieldParentKey;
                    targetValue = this.props.updatedObject && this.props.updatedObject[targetKey] ? this.props.updatedObject[targetKey] : null;
                    let answer = key && this.props.updatedObject && this.props.updatedObject[key] ? this.props.updatedObject[key] : null;
                    let validations = key && this.props.validations && this.props.validations[key] ? this.props.validations[key] : null;
                    return <React.Fragment>
                        {dataObj.questionHeader && dataObj.questionHeader.trim() && <div className={"questionnaire-header"}>{dataObj.questionHeader}</div>}
                        <Questionnaire
                            metaData={{ sectionKey: this.props.widgetData && this.props.widgetData.sectionKey }}
                            key={`genericEditor${index}`}
                            question={dataObj} answer={answer}
                            validations={validations}
                            templatedListValidations={(value: any, index: any) => this.templatedListValidations(value, index, key)}
                            updateHandler={(value: any, key: any) => this.updateHandler(value, key, dataObj)}
                            templatedListChange={(value: any, index: any) => this.itemChanged(value, index, key)}
                            templatedListRemoved={this.itemRemove}
                            hideComment={dataObj.hideComment ? dataObj.hideComment : true}
                            className={this.props.className}
                            isViewOnly={this.props.isViewOnly}
                            modalHeaders={this.props.modalHeaders}
                            fetchPatientPastSessions={this.props.fetchPatientPastSessions}
                            patientId={this.props.patientId}
                            subSectionKey={this.props.checkData.subSectionKey}
                            onAddNewItem={this.onAddNewItem}
                            addSymptomForReview={this.props.addSymptomForReview}
                            addButtonClicked={this.props.addButtonClicked}
                            responseData={this.props.responseData}
                            modalResponse={this.props.modalResponse}
                            complaints={this.props.updatedObject.complaints}
                            suggestions={this.props.suggestions}
                            targetKey={targetKey}
                            targetValue={targetValue}
                            updatedObject={this.props.updatedObject}
                            updateTemplatedListData={(data: { item: any, mode: FormItemMode }[], key: string) => this.handleUpdateTemplatedListData(data, key)}
                            validationSchema={validationSchema}
                            onTreatmentPlanSelect={this.props.onTreatmentPlanSelect}
                            onUndoApplyTreatmentPlan={this.props.onUndoApplyTreatmentPlan}
                            fetchDataOnSelect={this.props.fetchDataOnSelect}
                            emptyText={this.props.emptyText}
                            appointmentSchedule={this.props.appointmentSchedule}
                            getSuggestionsSequentially={this.props.getSuggestionsSequentially}
                            allQuestionsConfig={data}
                            hideDeleteAndAddOption={this.props.checkData.hideDeleteAndAddOption}
                        />
                    </React.Fragment>
                })}
            </div>
            {showPastHistory &&
                <div className={"past-examinations-container"}>
                    <a href="#" className="previous-sessions-link" onClick={() => { this.getPastSession(patientId, subSectionKey) }}>{showPastHistoryText}</a>
                </div>
            }
        </div>
    }
}

const mapStateToProps = (state: IAppState): GenericEditorStateToProps => {
    let showModal = state.responseData.showModal;
    let responseData = state.responseData.status;
    return { showModal, responseData };
}

const mapDispatchToProps = (dispatch: any): GenericEditorDispatchToProps => {
    return {
        toggleShowModal: (toggleModal: boolean) => {
            return dispatch(ModalActions.showModal(toggleModal));
        },
        updateModalUIConfig: (modalUIConfig: any[]) => {
            return dispatch(ModalActions.updateModalUIConfig(modalUIConfig))
        },
        addSymptomForReview: (name: string) => {
            return dispatch(ModalActions.addSymptomForReview(name))
        },
        addButtonClicked: (status: boolean, name: string) => {
            return dispatch(ModalActions.addButtonClicked(status, name))
        }
    }
}

export default connect<GenericEditorStateToProps, GenericEditorDispatchToProps, GenericEditorProps>(mapStateToProps, mapDispatchToProps)(GenericEditor)